/**
 * /app/trusts/$id/meetings — Trustee meeting log.
 *
 * Data: `GET /v1/trusts/{id}/meetings` via `useTrustMeetings`. A "Schedule
 * meeting" CTA opens the agenda-builder (`MeetingCreateForm`); "View details"
 * opens the governance-record modal (`MeetingDetailModal`).
 *
 * 2026-06-24 (app polish): a next-meeting hero band (white-label) leads with
 * the soonest scheduled meeting + the count on record; the list is grouped into
 * Upcoming / Past (the governance rhythm). Cards keep the real counts (agenda
 * items, attendees, resolutions tabled) + status; the rich record (agenda
 * decisions + attendee roster) lives in the detail modal.
 */

import { useState } from 'react';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { ScreenShell } from '../../../../components/screens/screen-shell';
import { TrustTabs } from '../../../../components/screens/trust-tabs';
import { Icon } from '../../../../components/icon';
import { MeetingCreateForm } from '../../../../components/screens/meeting-create-form';
import { MeetingDetailModal } from '../../../../components/screens/meeting-detail-modal';
import { useTrustMeetings, useCanActOnTrust } from '../../../../lib/api';
import type { TrustMeetingView } from '../../../../lib/api';
import { mapError } from '../../../../lib/error-messages';
// -- COHESION PASS, PHASE 5 (2026-09-15) ------------------------------------
// The meeting log was a run of cards, each with three italic-serif stat tiles
// and an uppercase status pill, under two hand-rolled uppercase group labels.
// It is now two bands, each holding one table: the same facts, scannable down a
// column instead of read card by card, which is what a log of meetings is for.
import { ConsoleBand } from '../../../../components/admin/console-band';
import { BleedTable, type BleedColumn, type BleedRow } from '../../../../components/bleed-table';
import { StateCard } from '../../../../components/state-card';

export const Route = createFileRoute('/app/trusts/$id/meetings')({
  component: MeetingsScreen,
});

function statusLabel(status: TrustMeetingView['status']): string {
  switch (status) {
    case 'scheduled':    return 'Scheduled';
    case 'in-progress':  return 'In progress';
    case 'finalised':    return 'Minutes signed';
  }
}

/** The chip tone, as one of the four the product has. A scheduled meeting is
 *  information, one in progress is information, signed minutes are a good
 *  outcome. Nothing here is a warning, so nothing takes a warning colour. */
function statusChipClass(status: TrustMeetingView['status']): string {
  switch (status) {
    case 'scheduled':    return 'trust-chip trust-chip--info';
    case 'in-progress':  return 'trust-chip trust-chip--info';
    case 'finalised':    return 'trust-chip trust-chip--positive';
  }
}

function formatMeetingDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return iso;
  }
}

// WS-4 register backfill: a small neutral chip marking a meeting that was
// auto-created from an ingested minutes document (rather than scheduled
// in-app). Matches the card's pill vocabulary (surface-2 / ink-3, uppercase
// like the status pill). Tooltip via `title`.
/** Sentence case, on the one chip shape. It marks a meeting auto-created from
 *  an ingested minutes document rather than scheduled in-app. */
function ImportedBadge() {
  return (
    <span className="trust-chip trust-chip--info" title="Imported from an uploaded document">
      Imported
    </span>
  );
}

/** The meeting-log columns. Three counts that used to be three italic-serif
 *  tiles per card are three numeric columns, so a reader comparing two meetings
 *  reads down instead of across two cards. */
const MEETING_COLUMNS: BleedColumn[] = [
  { key: 'meeting', header: 'Meeting' },
  { key: 'agenda', header: 'Agenda items', kind: 'num' },
  { key: 'attendees', header: 'Attendees', kind: 'num' },
  { key: 'resolutions', header: 'Resolutions tabled', kind: 'num' },
  { key: 'status', header: 'Status' },
  { key: 'open', header: 'View the meeting record', kind: 'action' },
];

/** One meeting. A trustee meeting is a named record with its own governance
 *  file, so the title is italic serif; its counts are amounts, so roman with
 *  tabular figures. An upcoming meeting carries the due-soon marker, which is
 *  the one urgency mechanism and replaces the 3px accent border the card had. */
function meetingRow(m: TrustMeetingView, onView: () => void): BleedRow {
  return {
    key: m.id,
    tone: m.status === 'finalised' ? 'routine' : 'soon',
    cells: {
      meeting: (
        <span style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span className="bleed-table-entity">{m.title}</span>
            {m.source === 'imported-document' ? <ImportedBadge /> : null}
          </span>
          <span className="bleed-table-sub">{formatMeetingDate(m.date)}</span>
        </span>
      ),
      agenda: <span className="trust-table-figure">{m.agendaCount}</span>,
      attendees: <span className="trust-table-figure">{m.attendees}</span>,
      resolutions: <span className="trust-table-figure">{m.resolutionsTabled}</span>,
      status: <span className={statusChipClass(m.status)}>{statusLabel(m.status)}</span>,
      open: (
        <button
          type="button"
          className="btn-secondary trust-row-action"
          data-testid={`meeting-view-${m.id}`}
          onClick={onView}
        >
          View details
        </button>
      ),
    },
  };
}

function MeetingsScreen() {
  const { id } = useParams({ from: '/app/trusts/$id/meetings' });
  // Tier-gate the write CTA: a beneficiary-tier member can read the meeting log
  // but cannot schedule a meeting (the create endpoint is trustee/writer-only).
  const canAct = useCanActOnTrust(id);

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [q, setQ] = useState('');

  const meetingsQ = useTrustMeetings({
    trustId: id,
    limit: 50,
    ...(from && { from }),
    ...(to && { to }),
    ...(q.trim() && { q: q.trim() }),
  });
  const filtersActive = !!(from || to || q.trim());

  const [createOpen, setCreateOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState<TrustMeetingView | null>(null);

  const meetings = meetingsQ.data?.meetings ?? [];
  const isLoading = meetingsQ.isLoading;
  const isError = meetingsQ.isError;

  // Group + find the next upcoming meeting (soonest scheduled / in-progress).
  const upcoming = meetings
    .filter((m) => m.status !== 'finalised')
    .slice()
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  const past = meetings
    .filter((m) => m.status === 'finalised')
    .slice()
    .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  const next = upcoming[0] ?? null;

  return (
    <ScreenShell
      activeNav="trusts"
      eyebrow="Trustee meetings"
      /* Roman, unpunctuated: a heading, not a named thing. */
      title="Meetings"
      sub="Quarterly cadence · minutes held on file"
      topbarRight={
        canAct ? (
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            aria-label="Schedule meeting"
            style={{
              background: 'var(--primary-700)', color: 'var(--surface)', border: 'none',
              padding: '10px 18px', borderRadius: 12, fontFamily: 'var(--sans)',
              fontSize: 14, fontWeight: 500, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 8,
            }}
          >
            <Icon name="plus" size={15} stroke={2} /> Schedule meeting
          </button>
        ) : undefined
      }
    >
      <MeetingCreateForm trustId={id} open={createOpen} onClose={() => setCreateOpen(false)} />
      <MeetingDetailModal
        trustId={id}
        meeting={selectedMeeting}
        open={selectedMeeting !== null}
        onClose={() => setSelectedMeeting(null)}
      />

      <TrustTabs trustId={id} activeTab="meetings" />

      {/* Next-meeting hero band — populated only (band carries the summary). */}
      {!isLoading && !isError && meetings.length > 0 && (
        <section
          style={{
            position: 'relative', overflow: 'hidden',
            background: 'linear-gradient(135deg, var(--primary-700), var(--primary-800))',
            borderRadius: 22, padding: '26px 30px', marginBottom: 24,
            display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 28, flexWrap: 'wrap',
            boxShadow: '0 26px 50px -32px color-mix(in srgb, var(--primary-800) 85%, transparent)',
          }}
        >
          <div aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'radial-gradient(130% 170% at 93% -30%, color-mix(in srgb, var(--accent-400) 16%, transparent), transparent 58%)' }} />
          <div style={{ position: 'relative', minWidth: 0 }}>
            <div className="eyebrow on-dark" style={{ marginBottom: 10 }}>
              {next ? 'Next meeting' : 'Trustee meetings'}
            </div>
            {next ? (
              <>
                {/* ROMAN: a date is not a named thing. */}
                <div className="serif" style={{ fontFamily: 'var(--serif)', fontWeight: 300, fontSize: 'clamp(26px, 3.6vw, 38px)', lineHeight: 1, color: 'var(--surface)', letterSpacing: '-0.01em' }}>
                  {formatMeetingDate(next.date)}
                </div>
                <div style={{ fontSize: 14, color: 'color-mix(in srgb, var(--surface) 74%, transparent)', marginTop: 9 }}>{next.title}</div>
              </>
            ) : (
              <div style={{ fontFamily: 'var(--serif)', fontWeight: 300, fontSize: 'clamp(24px, 3.2vw, 32px)', color: 'var(--surface)' }}>
                No meeting scheduled
              </div>
            )}
          </div>
          <div style={{ position: 'relative', textAlign: 'right' }}>
            <div style={{ fontFamily: 'var(--serif)', fontWeight: 300, fontSize: 28, color: 'var(--surface)', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{meetings.length}</div>
            {/* Sentence case: a caption under a figure is content, not a band
                label. */}
            <div style={{ fontSize: 11.5, color: 'color-mix(in srgb, var(--surface) 60%, transparent)', marginTop: 6 }}>on record</div>
          </div>
        </section>
      )}

      {/* Filters (#15) — server-side. */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, marginBottom: 22, flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, color: 'var(--ink-3)', fontWeight: 500 }}>Search title</span>
          <input
            type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. quarterly"
            aria-label="Search meetings by title" data-testid="meeting-search"
            style={{ padding: '8px 11px', borderRadius: 9, border: '1px solid var(--hairline-2)', background: 'var(--surface)', color: 'var(--ink)', fontFamily: 'var(--sans)', fontSize: 13.5, minWidth: 200 }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, color: 'var(--ink-3)', fontWeight: 500 }}>From</span>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="Meetings from date" data-testid="meeting-from"
            style={{ padding: '7px 10px', borderRadius: 9, border: '1px solid var(--hairline-2)', background: 'var(--surface)', color: 'var(--ink)', fontFamily: 'var(--sans)', fontSize: 13.5 }} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, color: 'var(--ink-3)', fontWeight: 500 }}>To</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} aria-label="Meetings to date" data-testid="meeting-to"
            style={{ padding: '7px 10px', borderRadius: 9, border: '1px solid var(--hairline-2)', background: 'var(--surface)', color: 'var(--ink)', fontFamily: 'var(--sans)', fontSize: 13.5 }} />
        </label>
        {filtersActive && (
          <button type="button" className="btn-secondary" data-testid="meeting-clear-filters" onClick={() => { setFrom(''); setTo(''); setQ(''); }} style={{ height: 36 }}>
            Clear
          </button>
        )}
      </div>

      {isLoading ? (
        /* Skeleton bars in the shape of the rows that are coming, and by
           construction no digit: three zeros where three counts belong read as
           a meeting log with nothing in it. */
        <StateCard
          variant="loading"
          testId="meetings-loading"
          label="Meetings"
          headline="Reading the trustee meetings on this trust."
        />
      ) : isError ? (
        <StateCard
          variant="error"
          testId="meetings-error"
          label="Meetings"
          headline="Meetings unavailable."
          detail={mapError(meetingsQ.error, 'load')}
        />
      ) : meetings.length === 0 ? (
        filtersActive ? (
          <StateCard
            variant="empty"
            testId="meetings-no-match"
            label="Meetings"
            headline="No meetings match these filters."
            detail="Clear the search or widen the dates to see the rest of the log."
            action={{
              label: 'Clear the filters',
              onClick: () => {
                setFrom('');
                setTo('');
                setQ('');
              },
            }}
          />
        ) : (
          <StateCard
            variant="empty"
            testId="meetings-empty"
            label="Meetings"
            headline="No trustee meetings recorded yet."
            detail="Meetings appear here once one is scheduled, or minutes are picked up from an uploaded document. Each entry tracks the agenda, attendees and resolutions tabled."
            {...(canAct
              ? { action: { label: 'Schedule a meeting', onClick: () => setCreateOpen(true) } }
              : {})}
          />
        )
      ) : (
        <>
          {upcoming.length > 0 && (
            <ConsoleBand
              id="meetings-upcoming"
              title="Upcoming"
              note={`${upcoming.length} ${upcoming.length === 1 ? 'meeting' : 'meetings'}`}
            >
              <div className="card trust-table-card trust-table-card--meetings">
                <BleedTable
                  testId="meetings-upcoming"
                  label="Upcoming trustee meetings"
                  columns={MEETING_COLUMNS}
                  rows={upcoming.map((m) => meetingRow(m, () => setSelectedMeeting(m)))}
                />
              </div>
            </ConsoleBand>
          )}
          {past.length > 0 && (
            <ConsoleBand
              id="meetings-past"
              title="Past meetings"
              note={`${past.length} ${past.length === 1 ? 'meeting' : 'meetings'}`}
            >
              <div className="card trust-table-card trust-table-card--meetings">
                <BleedTable
                  testId="meetings-past"
                  label="Past trustee meetings"
                  columns={MEETING_COLUMNS}
                  rows={past.map((m) => meetingRow(m, () => setSelectedMeeting(m)))}
                />
              </div>
            </ConsoleBand>
          )}
        </>
      )}
    </ScreenShell>
  );
}
